import {
  type ChangeEvent,
  type Dispatch,
  type DragEvent,
  type ReactNode,
  type Ref,
  type SetStateAction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";

import { OneDriveFileChooser } from "../OneDriveFileChooser";
import { FileDnDWrapper } from "./FileDnDWrapper";
import {
  type DragFile,
  type OnCompleteParams,
  type UploadState,
  allowedFileTypesList,
  uploadFile,
  uploadMultiFiles,
  verifyType,
} from "./uploadUtils";

export type FileUploadWrapperRef = {
  chooseFile: () => void;
  openOneDrive: () => void;
};

export type FileUploadWrapperProps = {
  accept?: string;
  forType: string;
  forId: string;
  setUploadState: Dispatch<SetStateAction<UploadState>>;
  setDraggedOverFiles: Dispatch<SetStateAction<Array<DragFile>>>;
  onError?: (message: string) => void;
  maxMBFileSize?: number;
  onComplete?: (
    savedName: OnCompleteParams["savedName"],
    fileName: OnCompleteParams["fileName"],
    signedDownloadRequest: OnCompleteParams["signedDownloadRequest"],
  ) => void;
  allowMultiple?: boolean;
  onCompleteMultiple?: (files: OnCompleteParams[]) => void;
  ref?: Ref<FileUploadWrapperRef>;
  children: ReactNode;
};

type FileInputEvent = ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>;

export const FileUploadWrapper = forwardRef(
  (
    {
      accept,
      forType,
      forId,
      onError,
      setUploadState,
      setDraggedOverFiles,
      onComplete,
      allowMultiple,
      onCompleteMultiple,
      maxMBFileSize = 100,
      children,
    }: FileUploadWrapperProps,
    ref,
  ) => {
    const [oneDriveOpen, setOneDriveOpen] = useState(false);
    const inputFile = useRef<HTMLInputElement>(null);

    const storageFileChosen = useCallback(
      (
        savedName: OnCompleteParams["savedName"],
        fileName: OnCompleteParams["fileName"],
        signedDownloadRequest: OnCompleteParams["signedDownloadRequest"],
      ) => {
        setOneDriveOpen(false);
        onComplete?.(savedName, fileName, signedDownloadRequest);
      },
      [onComplete],
    );

    useImperativeHandle(
      ref,
      () => ({
        chooseFile: () => inputFile.current?.click(),
        openOneDrive: () => setOneDriveOpen(true),
      }),
      [],
    );

    const getFilesFromEvent = useCallback((e: FileInputEvent) => {
      const fileList: FileList | undefined =
        "dataTransfer" in e ?
          e.dataTransfer?.files
        : (e.target?.files ?? undefined);

      const filesWithPreview: Array<File & { preview?: string }> = [];

      if (!fileList) {
        return filesWithPreview;
      }

      for (let i = 0; i < fileList.length; i += 1) {
        const file = fileList[i];
        if (!file) continue;
        const fileWithPreview = file as File & { preview?: string };
        fileWithPreview.preview = URL.createObjectURL(file);
        filesWithPreview.push(fileWithPreview);
      }

      return filesWithPreview;
    }, []);

    const uploadFiles = useCallback(
      (e: FileInputEvent) => {
        const uploadItems = getFilesFromEvent(e);
        if (!allowMultiple) {
          if (uploadItems.length > 1) {
            toast.error("You can only upload one file at a time");
            return;
          }
        }

        if (uploadItems.length > 5) {
          toast.error("You can only upload up to 5 files at once");
          return;
        }

        const allowedFiles: Array<File> = [];
        const errors: Array<string> = [];
        for (const item of uploadItems) {
          if (accept) {
            const { type } = item;
            const typeCheck = verifyType(accept, type);
            if (!typeCheck.allowed) {
              errors.push(typeCheck.error ?? "File type is not supported");
              continue;
            }
          }

          if (maxMBFileSize && item.size > maxMBFileSize * 1024 * 1024) {
            errors.push(
              `"${item.name}" is too large. Max file size is ${maxMBFileSize}MB`,
            );
            continue;
          }
          allowedFiles.push(item);
        }

        if (allowMultiple) {
          if (allowedFiles.length === 0) {
            for (const err of errors) {
              toast.error(err);
            }
            return;
          }
          if (errors.length > 0) {
            toast(
              `${errors.length} / ${uploadItems.length} files not supported, uploading ${allowedFiles.length} files`,
            );
          }
          uploadMultiFiles(
            allowedFiles,
            setUploadState,
            forType,
            forId,
            onCompleteMultiple,
            onError,
          );
        } else {
          const firstError = errors[0];
          if (firstError) {
            toast.error(firstError);
            return;
          }
          const firstFile = allowedFiles[0];
          if (!firstFile) {
            return;
          }
          uploadFile(
            firstFile,
            setUploadState,
            forType,
            forId,
            onComplete,
            onError,
          );
        }
      },
      [
        allowMultiple,
        getFilesFromEvent,
        accept,
        forType,
        forId,
        maxMBFileSize,
        onComplete,
        onCompleteMultiple,
        onError,
        setUploadState,
      ],
    );

    return (
      <>
        <input
          type="file"
          accept={accept ?? allowedFileTypesList.join(",")}
          style={{ display: "none" }}
          ref={inputFile}
          multiple={allowMultiple}
          onChange={uploadFiles}
        />

        <OneDriveFileChooser
          accept={accept}
          open={oneDriveOpen}
          onComplete={storageFileChosen}
          onError={(e) => {
            toast.error(e);
          }}
          forType={forType}
          forId={forId}
          onClose={() => {
            setOneDriveOpen(false);
          }}
        />

        <FileDnDWrapper
          handleSelectedFiles={uploadFiles}
          onError={onError}
          onDragOver={(e) => {
            const stableFiles: Array<DragFile> = [];
            for (let i = 0; i < e.dataTransfer.items.length; i++) {
              const item = e.dataTransfer.items[i];
              if (!item) continue;
              stableFiles.push({ kind: item.kind, type: item.type });
            }

            setDraggedOverFiles(stableFiles);
          }}
          onDragLeave={() => {
            setDraggedOverFiles([]);
          }}
        >
          {children}
        </FileDnDWrapper>
      </>
    );
  },
);

FileUploadWrapper.displayName = "FileUploadWrapper";
